// THE FLY CANVAS — Fly mode's view of the main area (docs/FLY_MODE.md; Nika
// 2026-10-17: "runs just like fly keepz", "make that flow easy, beautiful,
// clean, simple"). Every value comes from functions/assistantFly.js through
// `el.call`; the components hold no logic of their own.
//
// ONE column, top to bottom:
//   the bar      the Fly mark, the session's page count, New · Close
//   rows before  the session's earlier pages, folded into compact cards (Nika
//                2026-10-23: "add collapseable history when asking new fly
//                question in same thread like we have in fly keepz") — the
//                nearest three, the older ones behind one quiet line
//   THE PAGE     the open page (AssistantFlyPageBody): eyebrow · title · who
//                made it (or what it is doing now) · Ask again · Share · Save ·
//                the OPENING (streams; skeleton lines until its first
//                sentence) · the sections (the home grid's cells, the same
//                card bodies as a generated board) · the next asks
//   rows after   the pages after it, folded
// ONE page is open; a row opens its page (the others fold), and the open
// page's Fold folds it too — the thread then reads as its rows. The composer
// below is the shell's own: a send in Fly mode adds the next page.
//
// It replaces the main area the way a generated board does: a crossfade,
// opacity + visibility only (Rule 66 — closed keeps the box). Mounted on the
// home grid area (pages/main.js) and in the page layer over every other route
// (components/AssistantMainLayer.js, `mainHost: 'over'`). A CALL shows the same
// AssistantFlyPageBody on its stage (meet components/MeetFlyStage.js).
export const AssistantFlyView = {
  tag: 'section',
  role: 'region',
  dataFly: true,
  ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.region'),
  // The grid metrics — geometry of THIS element, so its own state (`start`:
  // the page is left-aligned beside the rail; every key declared, so a read
  // never falls back to a root key of the same name).
  // `older`: the thread's older rows shown (FlyOlder).
  state: { cols: 0, gutter: 0, cell: 0, square: true, start: false, older: false },

  dataOpen: (el, s) => (el.call('assistantFlyShown', s.root) ? 'true' : 'false'),
  ariaHidden: (el, s) => (el.call('assistantFlyShown', s.root) ? null : 'true'),

  display: 'flex',
  flexDirection: 'column',
  gap: 'B',
  width: '100%',
  // THE DASHBOARD'S WIDTH (Nika 2026-10-22: "make fly same width as
  // dashboard"): the view is the grid's own width, and its pages lay their
  // sections on the dashboard's grid (functions/assistantFly.js
  // assistantFlyMeasure). The conversation rail at the right edge (fixed, the
  // drawers' place) takes its room from the page alone: beside it the page
  // keeps the dashboard's left edge and ends a gutter before the rail —
  // measured, so a docked module rail at the left (bellforge's sidebar) and
  // the page layer over a route are measured too, never assumed.
  maxWidth: 'var(--home-grid-w, 100%)',
  marginInline: 'auto',
  minWidth: '0',
  boxSizing: 'border-box',
  paddingBottom: 'C',
  opacity: '0',
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity C defaultBezier, visibility 0s linear C',
  '&[data-open="true"]': {
    opacity: '1',
    visibility: 'visible',
    pointerEvents: 'auto',
    transition: 'opacity C defaultBezier, visibility 0s linear 0s'
  },

  subscribeTo: ['workspace', 'workspaceApps', 'workspaceProject'],
  onMounted: (el, s) => {
    el.call('assistantFlyMounted', el, s)
    el.call('assistantFlyShortcuts', el)
  },
  onUpdate: (el, s) => el.call('assistantFlyMounted', el, s),
  onWindowResize: (e, el, s) => el.call('assistantFlySyncGrid', el, s),
  onRemove: (el) => {
    if (typeof el.__flyShortcutsOff === 'function') el.__flyShortcutsOff()
    el.call('assistantBoardGridReadOff', el)
  },
  // The open page comes to the top of the view once, when it opens; the grid
  // is read again whenever the rail shows or leaves (the page takes its room
  // or gives it back — read a tick later, once the rail's box has changed).
  // A new page is the view's last part: the view takes the room it needs
  // below it (its `minHeight`, functions/assistantReveal.js) while it is on
  // screen, and gives it back when the session has no page open (New).
  minHeight: (el, s) =>
    el.call('assistantRevealRoom', el, s.root, el.call('assistantFlyShown', s.root)),
  stateDeps: [
    (el, s) => (s.root && s.root._aiFly ? s.root._aiFly.focus : null),
    (el, s) => !!el.call('assistantFlyRailShown', s.root)
  ],
  onStateUpdate: (el, s, ctx, change) => {
    const prev = (change && change.prev) || []
    const next = (change && change.next) || []
    if (prev[1] !== next[1]) el.call('assistantBoardGridRead', el, s, 'assistantFlySyncGrid')
    const key = next[0] || null
    if (key && prev[0] !== key && el.call('assistantFlyShown', s.root)) {
      el.call('assistantFlyReveal', el, key)
    } else if (!key && prev[0]) {
      el.call('assistantRevealRoomClear', el)
    }
  },

  // ── The bar ────────────────────────────────────────────────────────────────
  // On the home the switch above names the view and is the way out
  // (components/HomeViewSwitch.js): the bar keeps the count, the "Section
  // added" note and New, and steps aside while the start screen shows. No
  // tools here (Nika 2026-10-23): the top menu opens every app, and Add
  // section is a row of the composer's "+" menu.
  FlyBar: {
    hide: (el, s) =>
      !!el.call('assistantFlyOnHome', el) && !!el.call('assistantFlyStartShown', s.root),
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    minHeight: 'controlHCompact',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),

    FlyMark: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      color: 'title',
      Icon: {
        name: 'pxFly',
        boxSize: 'iconSm',
        flexShrink: '0',
        hide: (el) => !!el.call('assistantFlyOnHome', el)
      },
      FlyName: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '500',
        hide: (el) => !!el.call('assistantFlyOnHome', el),
        text: (el) => el.call('polyglot', 'ai.assistantMode.fly')
      },
      FlyCount: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'inactive',
        text: (el, s) => {
          const n = (el.call('assistantFlyPages', s.root) || []).length
          return n > 1
            ? String(el.call('polyglot', 'workspace.assistantFly.pageCount')).replace(
                '{n}',
                String(n)
              )
            : ''
        }
      }
    },

    FlyBarActions: {
      flow: 'x',
      flexWrap: 'wrap',
      align: 'center',
      gap: 'Y',
      minWidth: '0',
      AssistantFlyWorkspace: {},
      AppCapsuleBtnToolbar: {
        dataFlyNew: true,
        compact: true,
        // "New" goes back to the start screen (functions/assistantFlyStart.js)
        // — worth a button from the first page on. On a wide screen the
        // rail's head holds it.
        visibility: (el, s) =>
          (el.call('assistantFlyPages', s.root) || []).length > 0 ? 'visible' : 'hidden',
        '@tabletS<': { hide: true },
        text: (el) => el.call('polyglot', 'workspace.assistantFly.new'),
        onClick: (e, el) => el.call('assistantFlyWorkspaceNew')
      },
      // The canvas's own controls are the assistant's quiet icon button —
      // one geometry with the page's actions.
      AssistantIconButton: {
        dataFlyClose: true,
        // On the home the switch's Dashboard is the way out.
        hide: (el) => !!el.call('assistantFlyOnHome', el),
        ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.close'),
        title: (el) => el.call('polyglot', 'workspace.assistantFly.close'),
        onClick: (e, el) => el.call('assistantFlyWorkspaceDismiss', { focusComposer: true }),
        Icon: { name: 'x' }
      }
    }
  },

  // ── An empty session: the workspace's start screen ─────────────────────────
  // (components/AssistantFlyStart.js — its headline, its live figures and its
  // own asks, one tap from a page).
  AssistantFlyStart: {},

  // ── The thread's history: a wrapping shelf of the nearest three pages;
  // older pages wait behind one quiet line. Narrow surfaces keep one track.
  FlyOlder: {
    extends: 'AppControl',
    compact: true,
    tag: 'button',
    type: 'button',
    dataFlyOlder: true,
    hide: (el, s) => (el.call('assistantFlyRows', s.root, 'before') || []).length <= 3,
    ariaExpanded: (el, s) => (s.older === true ? 'true' : 'false'),
    flow: 'x',
    align: 'center',
    gap: 'Y',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    padding: '0',
    border: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 'Z2',
    color: 'inactive',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { color: 'title' },
    ':active': { color: 'title' },
    ':focus-visible': { shadow: 'focusRing' },
    onClick: (e, el, s) => s.update({ older: s.older !== true }),
    Icon: {
      name: (el, s) => (s.older === true ? 'chevronUp' : 'chevronDown'),
      boxSize: 'iconSm',
      flexShrink: '0'
    },
    OlderText: {
      tag: 'span',
      text: (el, s) => {
        if (s.older === true) return el.call('polyglot', 'workspace.assistantFly.fewer')
        const n = (el.call('assistantFlyRows', s.root, 'before') || []).length - 3
        return String(el.call('polyglot', 'workspace.assistantFly.earlier')).replace(
          '{n}',
          String(Math.max(0, n))
        )
      }
    }
  },
  FlyBefore: {
    tag: 'ol',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    // A wrapping shelf keeps nearby pages in reach without pushing the
    // active page down by a full row for every item. Tracks fit the actual
    // page width, including a narrow PageSlot or the conversation rail.
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F)), 1fr))',
    gap: 'Z',
    '@mobileL': {
      gridTemplateColumns: 'none',
      gridAutoFlow: 'column',
      gridAutoColumns: 'minmax(85%, 1fr)',
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
      scrollSnapType: 'x proximity',
      paddingBottom: 'X'
    },
    minWidth: '0',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    children: (el, s) => {
      const rows = el.call('assistantFlyRows', s.root, 'before') || []
      return s.older === true ? rows : rows.slice(-3)
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyRow'
  },

  // ── The open page — ONE child keyed by its page: a newly opened page is a
  // new element (it rises in), its sections new cards. The child's state is
  // the page + where it lives (`source`) + this view's grid metrics.
  FlyOpen: {
    minWidth: '0',
    children: (el, s) => {
      // the thread folded: every page is its row (FlyBefore)
      if (el.call('assistantFlyFolded', s.root)) return []
      const p = el.call('assistantFlyPage', s.root)
      if (!p) return []
      const m = el.call('assistantBoardMetrics', s)
      // On the page layer over a route the ground is the layer's sheet, not
      // the home's sky: its cards take the solid card fill.
      const onLayer = !!(el.parent && el.parent.mainHost === 'over')
      return [
        {
          ...p,
          source: 'session',
          cols: m.cols,
          gutter: m.gutter,
          cell: m.cell,
          square: m.square,
          // beside the rail: the dashboard's left edge (assistantFlyPageStyle)
          start: s.start === true,
          onLayer
        }
      ]
    },
    childrenAs: 'state',
    childExtends: 'AssistantFlyPageBody'
  },

  // ── The later pages, folded ───────────────────────────────────────────────
  FlyAfter: {
    tag: 'ol',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, var(--spacing-F)), 1fr))',
    gap: 'Z',
    '@mobileL': {
      gridTemplateColumns: 'none',
      gridAutoFlow: 'column',
      gridAutoColumns: 'minmax(85%, 1fr)',
      overflowX: 'auto',
      overscrollBehaviorX: 'contain',
      scrollSnapType: 'x proximity',
      paddingBottom: 'X'
    },
    minWidth: '0',
    width: '100%',
    marginInline: 'auto',
    boxSizing: 'border-box',
    style: (el, s) => el.call('assistantFlyPageStyle', s),
    children: (el, s) => el.call('assistantFlyRows', s.root, 'after'),
    childrenAs: 'state',
    childExtends: 'AssistantFlyRow'
  },

  // ── The conversation, at the right — or, put away, its lines ─────────────
  AssistantFlyRail: {},
  AssistantFlyRailMarks: {}
}

// THE LINES (Nika 2026-10-22: "make fly sidebar toggleable, show lines when
// its hidden"): the rail put away leaves the session at the right edge, the
// way a long page keeps its outline — one short line per page, oldest at the
// top as on the rail; the open page's line the longest, in the title ink; a
// page still being made breathes. A line opens its page (its title is its
// name and its tooltip); the button above the lines brings the rail back.
// Fixed in the rail's own column, centred on the screen's height: putting the
// rail away or back moves nothing on the page (Rule 66). Wide screens only,
// like the rail.
export const AssistantFlyRailMarks = {
  tag: 'nav',
  dataFlyRailMarks: true,
  ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
  hide: (el, s) => !el.call('assistantFlyRailMarksShown', s.root),
  '@tabletS': { hide: true },
  position: 'fixed',
  right: 'X2',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: '3',
  flow: 'y',
  align: 'flex-end',
  gap: 'Y',
  maxHeight: 'calc(100vh - var(--shell-top) - var(--spacing-D1))',
  animation: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',

  // A new chat, as the rail's head offers it — with the rail put away a wide
  // screen had no way back to the start screen (its bar's New is the narrow
  // screens'; dev 2026-10-22).
  AssistantIconButton_new: {
    dataFlyMarksNew: true,
    ariaLabel: (el) => el.call('polyglot', 'ai.chat.newChat'),
    title: (el) => el.call('polyglot', 'ai.chat.newChat'),
    onClick: (e, el) => {
      el.call('assistantFlyWorkspaceNew')
      el.call('assistantBoardFocus', 'composer')
    },
    Icon: { name: 'plus' }
  },

  AssistantIconButton: {
    dataFlyRailShow: true,
    ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
    ariaExpanded: 'false',
    title: (el) => el.call('polyglot', 'ai.chat.history'),
    onClick: (e, el) => el.call('assistantFlyRailToggle', false),
    Icon: { name: 'chevronLeft' }
  },

  MarksList: {
    tag: 'ol',
    margin: '0',
    padding: '0',
    listStyle: 'none',
    flow: 'y',
    align: 'flex-end',
    minHeight: '0',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    children: (el, s) => el.call('assistantFlyRailMarks', s.root),
    childrenAs: 'state',
    childExtends: 'AssistantFlyRailMark'
  }
}

// One line: a button the width of the rail's toggle (an easy target), the
// line itself at its right end.
export const AssistantFlyRailMark = {
  tag: 'li',
  display: 'flex',
  justifyContent: 'flex-end',
  MarkButton: {
    extends: 'TitleTooltipTrigger',
    tag: 'button',
    type: 'button',
    dataFlyMark: (el, s) => (s && s.pageKey) || null,
    dataOpen: (el, s) => (s && s.open ? 'true' : null),
    dataBusy: (el, s) => (s && s.busy ? 'true' : null),
    ariaCurrent: (el, s) => (s && s.open ? 'page' : null),
    ariaLabel: (el, s) => (s && s.title) || null,
    title: (el, s) => (s && s.title) || '',
    display: 'flex',
    align: 'center flex-end',
    width: 'controlHCompact',
    minHeight: 'B',
    padding: '0',
    border: 'none',
    background: 'transparent',
    color: 'inactive',
    cursor: 'pointer',
    round: 'radiusControl',
    transition: 'A defaultBezier',
    transitionProperty: 'color',
    ':hover': { color: 'title' },
    ':focus-visible': { shadow: 'focusRing' },
    '&[data-open="true"]': { color: 'title' },
    onClick: (e, el, s) => el.call('assistantFlyFocus', s && s.pageKey),
    MarkLine: {
      tag: 'span',
      ariaHidden: 'true',
      display: 'block',
      width: 'Z',
      height: 'hairline2',
      round: 'radiusPill',
      background: 'currentColor',
      transition: 'A defaultBezier',
      transitionProperty: 'width',
      ':is([data-fly-mark]:hover) &': { width: 'A' },
      ':is([data-fly-mark][data-open="true"]) &': { width: 'B' },
      ':is([data-fly-mark][data-busy="true"]) &': {
        animation: 'pulse',
        animationDuration: 'E',
        animationIterationCount: 'infinite'
      }
    }
  }
}

// THE RAIL — the session as a conversation at the right edge, as keepz--fly
// keeps its chat history: every ask and its answer (the page's opening) in the
// workspace's own chat rows, each with the way back to its page. The drawers'
// place and chrome (the messenger's list, the home's Customize drawer: fixed,
// the shell's top line, the glass card), so every right rail stands in one
// spot. Wide screens only; never under a page sheet.
export const AssistantFlyRail = {
  tag: 'aside',
  dataFlyRail: true,
  ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
  hide: (el, s) => !el.call('assistantFlyRailShown', s.root),
  '@tabletS': { hide: true },

  position: 'fixed',
  top: '--shell-top',
  right: 'X2',
  bottom: 'D1',
  width: 'G1',
  minWidth: 'G1',
  maxWidth: 'G1',
  zIndex: '3',
  theme: 'canvas-card-glass',
  borderWidth: '0',
  round: 'radiusCard',
  flow: 'y',
  overflow: 'hidden',
  boxSizing: 'border-box',
  animation: 'fadeIn',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',

  RailHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    flexShrink: '0',
    minHeight: 'controlH',
    padding: 'Y Z Y A',
    RailTitle: {
      tag: 'h2',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '500',
      color: 'title',
      text: (el) => el.call('polyglot', 'ai.chat.history')
    },
    RailHeadActions: {
      flow: 'x',
      align: 'center',
      gap: 'X',
      flexShrink: '0',
      AssistantIconButton: {
        dataFlyRailNew: true,
        ariaLabel: (el) => el.call('polyglot', 'ai.chat.newChat'),
        title: (el) => el.call('polyglot', 'ai.chat.newChat'),
        onClick: (e, el) => {
          el.call('assistantFlyWorkspaceNew')
          el.call('assistantBoardFocus', 'composer')
        },
        Icon: { name: 'plus' }
      },
      // Put the rail away — the session stays at the edge as lines
      // (AssistantFlyRailMarks). One toggle, named by what it shows; its
      // state is `aria-expanded`.
      AssistantIconButton_hide: {
        dataFlyRailHide: true,
        ariaLabel: (el) => el.call('polyglot', 'ai.chat.history'),
        ariaExpanded: 'true',
        title: (el) => el.call('polyglot', 'ai.chat.history'),
        onClick: (e, el) => el.call('assistantFlyRailToggle', true),
        Icon: { name: 'chevronRight' }
      }
    }
  },

  // The lines stand at the bottom (the newest above the composer's line of
  // sight) and scroll up into the older ones: a REVERSED column scroller
  // keeps its end in view by itself — a new line, a growing answer — and a
  // person who scrolled up to read stays where they are.
  RailScroll: {
    flex: '1 1 auto',
    minHeight: '0',
    display: 'flex',
    flexDirection: 'column-reverse',
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    padding: '0 Z A',
    RailLines: {
      tag: 'ol',
      margin: '0',
      padding: '0',
      listStyle: 'none',
      flow: 'y',
      gap: 'Z1',
      flexShrink: '0',
      boxSizing: 'border-box',
      children: (el, s) => el.call('assistantFlyRailLines', s.root),
      childrenAs: 'state',
      childExtends: 'AssistantFlyRailLine'
    }
  }
}

// One line of the rail: a chat row (the ask, or the answer) or a page's foot.
// The row is the workspace's ChatMessage itself — the sidebar's and the main
// thread's — so a fly conversation reads like every other one.
export const AssistantFlyRailLine = {
  tag: 'li',
  minWidth: '0',
  dataFlyLine: (el, s) => (s && s.key) || null,
  ChatMessage: {
    if: (el, s) =>
      !!s && s.kind === 'message' && (s.role !== 'assistant' || !!s.text || !!s.streaming)
  },
  AssistantFlyRailFoot: {
    if: (el, s) => !!s && s.kind === 'foot'
  }
}

// A page's foot: the way back to it ("Show page"; the open one needs none)
// and what it is — its sections and its time.
export const AssistantFlyRailFoot = {
  flow: 'x',
  align: 'center',
  gap: 'Z',
  minWidth: '0',
  paddingLeft: 'A',
  marginTop: '-Y',
  fontSize: 'Z1',
  color: 'inactive',
  FootShow: {
    tag: 'button',
    type: 'button',
    dataFlyShow: (el, s) => (s && s.pageKey) || null,
    hide: (el, s) => !s || !!s.open || !!s.failed,
    flow: 'x',
    align: 'center',
    gap: 'X',
    padding: '0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'title',
    fontSize: 'Z1',
    fontWeight: '500',
    textDecoration: 'underline',
    textUnderlineOffset: '0.2em',
    ':hover': { color: 'highlight' },
    ':active': { opacity: '.7' },
    onClick: (e, el, s) => el.call('assistantFlyFocus', s.pageKey),
    FootShowLabel: {
      tag: 'span',
      text: (el) => el.call('polyglot', 'workspace.assistantFly.showPage')
    },
    Icon: { name: 'arrowRight', boxSize: 'Z' }
  },
  FootMeta: {
    tag: 'span',
    text: (el, s) => el.call('assistantFlyRailMeta', s)
  }
}

// ONE PAGE — its state IS the page (functions/assistantFly.js page shape) +
// `source` ('session' | 'call') + the host's grid metrics (cols, gutter,
// cell, square). The canvas and a call's stage show it the same way; its
// actions go through assistantFlyPageAct, which knows where the page lives.
export const AssistantFlyPageBody = {
  tag: 'article',
  dataFlyPage: (el, s) => (s && s.key) || null,
  // Its sections' width, centred (functions/assistantFly.js
  // assistantFlyPageStyle); brought into view, its head stays clear of the
  // shell's floating header.
  width: '100%',
  marginInline: 'auto',
  style: (el, s) => el.call('assistantFlyPageStyle', s),
  scrollMarginTop: 'calc(var(--shell-top) + var(--spacing-A))',
  ariaBusy: (el, s) => (s && (s.status === 'loading' || s.status === 'streaming') ? 'true' : null),
  ariaLabel: (el, s) => (s && (s.title || s.prompt)) || '',
  flow: 'y',
  gap: 'B',
  minWidth: '0',
  // A page rises in once (opacity + the brand's short travel).
  animation: 'fadeInUpShort',
  animationDuration: 'C',
  animationTimingFunction: 'defaultBezier',

  FlyHead: {
    flow: 'y',
    gap: 'Z',
    minWidth: '0',
    boxSizing: 'border-box',
    padding: 'A1 B',
    round: 'radiusCard',
    background: (el, s) =>
      s && s.onLayer ? 'cardSolid' : 'var(--home-card-bg, var(--theme-card-background))',
    '@mobileL': { padding: 'A' },

    FlyEyebrow: {
      tag: 'p',
      // The line keeps its room while the head is on its way; a head without
      // one (or a page made from a board) gives the room back.
      hide: (el, s) => !s || (!s.eyebrow && (!!s.title || !!s.fallback)),
      margin: '0',
      maxWidth: 'var(--spacing-I)',
      minHeight: '1.3em',
      fontSize: 'Z',
      fontWeight: '500',
      lineHeight: '1.3',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'inactive',
      text: (el, s) => (s && s.eyebrow) || ''
    },

    // The title: the page's own once it comes; until then the ask itself, in
    // the quiet ink — the page has its words from the first frame.
    FlyTitle: {
      tag: 'h2',
      margin: '0',
      maxWidth: 'var(--spacing-I)',
      fontSize: 'D',
      fontWeight: '700',
      lineHeight: '1.15',
      textWrap: 'balance',
      overflowWrap: 'anywhere',
      '@mobileL': { fontSize: 'C' },
      color: (el, s) => (s && s.title ? 'title' : 'inactive'),
      transition: 'color C defaultBezier',
      text: (el, s) => (s && (s.title || s.prompt)) || ''
    },

    // ONE line: what the page is doing now (a breathing dot), then — the
    // same box — who composed it and when. The actions sit at its end and
    // keep their box while the page is made (Rule 66).
    FlyMeta: {
      flow: 'x',
      align: 'center space-between',
      flexWrap: 'wrap',
      gap: 'Z',
      minWidth: '0',
      minHeight: 'controlHCompact',

      FlyByline: {
        role: 'status',
        flow: 'x',
        align: 'center',
        gap: 'Y',
        minWidth: '0',
        fontSize: 'Z2',
        color: 'inactive',
        FlyDot: {
          boxSize: 'Y',
          round: 'radiusPill',
          background: 'currentColor',
          flexShrink: '0',
          animation: 'pulse',
          animationDuration: 'E',
          animationIterationCount: 'infinite',
          hide: (el, s) => !s || (s.status !== 'loading' && s.status !== 'streaming')
        },
        FlyBylineText: {
          tag: 'span',
          text: (el, s) => {
            if (!s) return ''
            const key = el.call('assistantFlyStatusKey', s)
            if (key && s.status !== 'failed') return el.call('polyglot', key)
            return s.status === 'ready' ? el.call('assistantFlyByline', s) : ''
          }
        }
      },

      FlyActions: {
        flow: 'x',
        align: 'center',
        gap: 'X',
        flexShrink: '0',
        visibility: (el, s) => (s && s.status === 'ready' ? 'visible' : 'hidden'),
        // Fold: the open page folds into its row too (keepz--fly's fold)
        AssistantIconButton_fold: {
          dataFlyFold: true,
          hide: (el, s) => !s || s.source !== 'session',
          ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.fold'),
          title: (el) => el.call('polyglot', 'workspace.assistantFly.fold'),
          onClick: (e, el) => el.call('assistantFlyFold'),
          Icon: { name: 'chevronUp' }
        },
        AssistantIconButton: {
          dataFlyAgain: true,
          hide: (el, s) => !s || !s.prompt,
          ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.again'),
          title: (el) => el.call('polyglot', 'workspace.assistantFly.again'),
          onClick: (e, el, s) => el.call('assistantFlyPageAct', s, 'again'),
          Icon: { name: 'refresh' }
        },
        AssistantIconButton_share: {
          dataFlyShare: true,
          hide: (el, s) => !s || !s.prompt || s.source === 'call',
          ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.share'),
          title: (el) => el.call('polyglot', 'workspace.assistantFly.share'),
          onClick: (e, el, s) => el.call('assistantFlyPageAct', s, 'share'),
          Icon: { name: 'share' }
        },
        AssistantIconButton_save: {
          dataFlySave: true,
          // A page of only UI sections (one read's result each) has nothing
          // a board could read again (functions/assistantFly.js).
          disabled: (el, s) =>
            !s || !el.call('assistantFlySavable', s) || s.save === 'saving' || s.save === 'saved'
              ? true
              : null,
          ariaLabel: (el, s) =>
            el.call(
              'polyglot',
              s && s.save === 'saved'
                ? 'workspace.assistantFly.saved'
                : 'workspace.assistantFly.save'
            ),
          title: (el, s) =>
            el.call(
              'polyglot',
              s && s.save === 'saved'
                ? 'workspace.assistantFly.saved'
                : 'workspace.assistantFly.save'
            ),
          onClick: (e, el, s) => el.call('assistantFlyPageAct', s, 'save'),
          Icon: { name: (el, s) => (s && s.save === 'saved' ? 'check' : 'bookmark') }
        }
      }
    }
  },

  // THE OPENING — the page's answer in a few sentences, every number from the
  // page's own data. Two skeleton lines hold its place until the first
  // sentence; a caret blinks while it is still being written.
  FlyOpening: {
    tag: 'p',
    dataFlyOpening: true,
    ariaLive: 'polite',
    margin: '0',
    maxWidth: 'I',
    fontSize: 'B',
    lineHeight: '1.5',
    color: 'paragraph',
    hide: (el, s) => !s || s.status === 'failed' || (s.status === 'ready' && !s.opening),
    FlyOpeningText: {
      tag: 'span',
      text: (el, s) => (s && s.opening) || ''
    },
    FlyCaret: {
      tag: 'span',
      ariaHidden: 'true',
      display: 'inline-block',
      width: 'X',
      height: '1em',
      marginLeft: 'X',
      verticalAlign: 'text-bottom',
      background: 'currentColor',
      animation: 'blinking',
      animationDuration: 'E',
      animationIterationCount: 'infinite',
      hide: (el, s) => !s || !s.opening || s.status === 'ready' || s.status === 'failed'
    },
    FlySkeleton: {
      flow: 'y',
      gap: 'Y',
      paddingTop: 'X',
      hide: (el, s) => !s || !!s.opening || s.status === 'ready' || s.status === 'failed',
      Skeleton_1: { width: '100%', height: 'A' },
      Skeleton_2: { width: '62%', height: 'A' }
    }
  },

  // A CHANGE the ask asks for: the sentence and ONE action — the assistant
  // does it with its confirm cards (functions/assistantFly.js
  // assistantFlyHandoff). The page above it shows what the change touches.
  //
  // When the change is one an app's OWN FORM makes (the page's `form`), the
  // one action opens that form, prefilled (assistantFlyFormOpen — the
  // answer's form path; nothing is written until the member saves there).
  // A form the workspace cannot open says so in one plain line instead
  // (assistantFlyFormNote) — no dead button, no blank panel.
  FlyAction: {
    role: 'group',
    dataFlyAction: true,
    ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.actionLabel'),
    hide: (el, s) =>
      !s || s.status !== 'ready' || !(s.action || (s.form && s.form.form)) || s.source === 'call',
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center space-between',
    gap: 'Z',
    padding: 'Z A',
    round: 'radiusCard',
    background: 'var(--home-card-bg, var(--theme-card-background))',
    ActionText: {
      tag: 'span',
      flex: '1 1 F',
      minWidth: '0',
      fontSize: 'Z2',
      color: 'title',
      text: (el, s) => {
        void (s && s.form)
        const note = el.call('assistantFlyFormNote', s)
        return note || (s && s.action) || ''
      }
    },
    AppCapsuleBtnPrimary: {
      dataFlyDoIt: true,
      hide: (el, s) => !!(s && s.form && s.form.form),
      text: (el) => el.call('polyglot', 'workspace.assistantFly.doIt'),
      onClick: (e, el, s) => el.call('assistantFlyHandoff', s)
    },
    AppCapsuleBtnPrimary_form: {
      dataFlyFormOpen: true,
      hide: (el, s) => {
        const root = (s && s.root) || {}
        void root.workspaceApps
        void root.workspaceProject
        return el.call('assistantFlyFormStatus', s) !== 'ready'
      },
      text: (el) => el.call('polyglot', 'workspace.assistantBlock.formReview'),
      onClick: (e, el, s) => el.call('assistantFlyFormOpen', s)
    }
  },

  // A failure: one sentence and Try again; the session's pages stay.
  //
  // A failure for CREDITS is not a fault, and Try again cannot mend it — the
  // server says "retrying will not help" — so that button never shows for it
  // (a Georgian visitor met exactly that: the sentence, and a "try again"
  // that returned the same one; persona audit round 1). In its place an admin
  // gets the way out (Add credits → the credit page) and a member gets who to
  // ask. Every other failure is unchanged.
  FlyFailure: {
    role: 'alert',
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Z',
    hide: (el, s) => !s || s.status !== 'failed',
    FailureText: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'paragraph',
      text: (el, s) => {
        const key = el.call('assistantFlyStatusKey', s)
        if (!key) return ''
        const text = el.call('polyglot', key)
        return s && s.error === 'credits' && !el.call('currentUserIsAdmin', s.root)
          ? `${text} ${el.call('polyglot', 'workspace.credits.chat.balanceMember')}`
          : text
      }
    },
    AppCapsuleBtnToolbar: {
      compact: true,
      hide: (el, s) => !!s && s.error === 'credits',
      text: (el) => el.call('polyglot', 'workspace.assistantFly.retry'),
      onClick: (e, el, s) => el.call('assistantFlyPageAct', s, 'again')
    },
    AppCapsuleBtnToolbar_1: {
      compact: true,
      hide: (el, s) => !s || s.error !== 'credits' || !el.call('currentUserIsAdmin', s.root),
      text: (el) => el.call('polyglot', 'workspace.creditBanner.addCredits'),
      onClick: (e, el) => el.router('/admin/usage', el.getRoot())
    }
  },

  // THE SECTIONS — the home grid's cells (the same metrics, the same card
  // bodies as a generated board); skeletons hold the sections still coming.
  FlyGrid: {
    dataFlyGrid: true,
    display: 'grid',
    gridAutoFlow: 'row dense',
    justifyContent: 'center',
    minWidth: '0',
    style: (el, s) => el.call('assistantFlyGridStyle', s),
    children: (el, s) => el.call('assistantFlyStableCells', s, s && s.root, s && s.cols),
    childrenAs: 'state',
    childExtends: 'AssistantFlyCard'
  },

  // THE NEXT ASKS — one tap is the next page (of the session, or of the call);
  // on a page about what Symbols is or where to start, the quick tour too —
  // one tap starts it (functions/assistantFlyFollow.js).
  FlyFollow: {
    role: 'group',
    ariaLabel: (el) => el.call('polyglot', 'workspace.assistantFly.next'),
    flow: 'x',
    flexWrap: 'wrap',
    align: 'center',
    gap: 'Y',
    minWidth: '0',
    animation: 'fadeIn',
    animationDuration: 'C',
    animationTimingFunction: 'defaultBezier',
    hide: (el, s) => !(el.call('assistantFlyFollowChips', s) || []).length,
    FollowLabel: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'inactive',
      marginRight: 'X',
      text: (el) => el.call('polyglot', 'workspace.assistantFly.next')
    },
    FollowChips: {
      flow: 'x',
      flexWrap: 'wrap',
      gap: 'Y',
      minWidth: '0',
      // Each chip carries where its page lives, so its ask goes to the same
      // place (the session, or the call and its agent); the tour chip runs
      // the tour.
      children: (el, s) => el.call('assistantFlyFollowChips', s),
      childrenAs: 'state',
      childExtends: 'AppCapsuleBtnToolbar',
      childProps: {
        compact: true,
        whiteSpace: 'normal',
        title: (el, s) => (s && s.prompt) || '',
        text: (el, s) => (s && s.label) || '',
        onClick: (e, el, s) => el.call('assistantFlyFollowPress', s)
      }
    }
  }
}

// A folded page (keepz--fly's folded row, Nika 2026-10-23: "collapseable
// history … like we have in fly keepz"): ONE card row — its place in the
// thread, eyebrow · title · "14:05 · 6 sections", and Open; the whole row
// opens it (the others fold). While its page is still being made (an ask sent
// over it), a breathing dot. The start screen's press is its surface: the
// house card, an inset wash on hover (functions/assistantFlyStart.js).
export const AssistantFlyRow = {
  tag: 'li',
  minWidth: '0',
  scrollSnapAlign: 'start',
  FlyRowButton: {
    extends: 'AssistantFlyStartPress',
    dataFlyRow: (el, s) => (s && s.key) || null,
    ariaLabel: (el, s) => (s && (s.title || s.prompt)) || null,
    title: (el, s) => (s && (s.title || s.prompt)) || null,
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: 'Z',
    height: '100%',
    minHeight: 'controlH',
    padding: 'Z',
    round: 'radiusCard',
    animation: 'fadeIn',
    ':focus-visible': { shadow: 'focusRingInset' },
    animationDuration: 'C',
    animationTimingFunction: 'defaultBezier',
    onClick: (e, el, s) => el.call('assistantFlyPageAct', s, 'focus'),

    // its place in the thread: 1, 2, 3 …
    RowMark: {
      tag: 'span',
      ariaHidden: 'true',
      theme: 'layer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      boxSize: 'controlHCompact',
      round: 'radiusControl',
      fontSize: 'Z2',
      fontWeight: '700',
      fontVariantNumeric: 'tabular-nums',
      color: 'title',
      text: (el, s) => (s && Number.isFinite(Number(s.index)) ? String(Number(s.index) + 1) : '')
    },

    RowText: {
      flow: 'y',
      gap: 'X',
      minWidth: '0',
      RowEyebrow: {
        tag: 'span',
        hide: (el, s) => !s || !s.eyebrow,
        minWidth: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: 'Z',
        fontWeight: '500',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'inactive',
        text: (el, s) => (s && s.eyebrow) || ''
      },
      RowTitle: {
        tag: 'span',
        minWidth: '0',
        lineClamp: '2',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        overflowWrap: 'anywhere',
        fontSize: 'Z2',
        fontWeight: '500',
        lineHeight: '1.25',
        text: (el, s) => (s && (s.title || s.prompt)) || ''
      },
      RowMeta: {
        flow: 'x',
        align: 'center',
        gap: 'Y',
        minWidth: '0',
        fontSize: 'Z',
        color: 'inactive',
        RowDot: {
          boxSize: 'Y',
          round: 'radiusPill',
          background: 'currentColor',
          flexShrink: '0',
          animation: 'pulse',
          animationDuration: 'E',
          animationIterationCount: 'infinite',
          hide: (el, s) => !s || (s.status !== 'loading' && s.status !== 'streaming')
        },
        RowWhen: {
          tag: 'span',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          text: (el, s) => el.call('assistantFlyRowMeta', s) || ''
        }
      }
    },

    // "Open ⌄" — the row unfolds into its page
    RowOpen: {
      tag: 'span',
      flow: 'x',
      align: 'center',
      gap: 'Y',
      flexShrink: '0',
      padding: 'X',
      round: 'radiusPill',
      background: 'selected',
      fontSize: 'Z',
      fontWeight: '500',
      RowOpenText: {
        tag: 'span',
        '@tabletS': { hide: true },
        text: '{{ common.action.open | polyglot }}'
      },
      Icon: { name: 'chevronDown', boxSize: 'iconSm', flexShrink: '0' }
    }
  }
}

// A section of a page: the generated board's card (the home widget anatomy,
// the same body mount — a widget, a records block, a word block), without the
// board's own Pin and Close: the page's actions are the page's (Save).
export const AssistantFlyCard = {
  extends: 'AssistantBoardCard',
  dataFlySection: (el, s) => (s && s.key) || null,
  // A widget or view section is a widget REGION for Inspect & ask
  // (functions/aiInspect.js): its id, app and params ride the ask.
  dataFlyWidget: (el, s) => (s && s.kind === 'widget' && s.id ? s.id : null),
  // The board card's span — until the section is known to be EMPTY: then the
  // same columns and one row, never one line over a blank square (functions/
  // assistantFly.js assistantFlyCardStyle).
  style: (el, s) => el.call('assistantFlyCardStyle', el, s),
  animation: 'fadeInUpShort',
  // On a sheet (the page layer over a route, a call's stage) the default glass
  // card is near-white on a near-white ground in the light theme: there the
  // card is the SOLID skin's (`cardSolid`, a step darker than the sheet in
  // light, the solid dark card in dark). On the home it keeps the house card
  // over the sky.
  background: (el, s) =>
    s && s.onLayer ? 'cardSolid' : 'var(--home-card-bg, var(--theme-card-background))',
  CardHead: {
    // The title opens the section's app, as a home card's does — a view's
    // own page with the filters the view resolved, a widget's route, a
    // records card's collection (assistantBoardCardOpen).
    CardTitle: {
      cursor: (el, s) => (el.call('homeWidgetOpenRoute', s) ? 'pointer' : 'inherit'),
      onClick: (e, el, s) => {
        if (!el.call('homeWidgetOpenRoute', s)) return
        e.stopPropagation()
        el.call('assistantBoardCardOpen', s, e)
      }
    },
    // The ONE square button is the home card's (a page is read-only, so never
    // a Pin): the widget's own verb when it declares one — "+ New deal" opens
    // the deal form, "Open follow-ups" the follow-ups view — else ↗ Open, the
    // section's app (a view's own page with the filters the view resolved, a
    // records card's collection) in the sheet; with ⌘/Ctrl, in a split
    // (functions/assistantFly.js assistantFlyCardAct).
    HomeWidgetAction: {
      dataBoardPin: null,
      dataFlyOpen: (el, s) => (el.call('assistantFlyCardVerb', s) ? null : true),
      // `data-fly-verb`, never `data-fly-action`: that names the page's own
      // change-and-Do-it group (FlyAction above).
      dataFlyVerb: (el, s) => (el.call('assistantFlyCardVerb', s) ? true : null),
      hide: (el, s) => !el.call('assistantFlyCardActionLabel', s),
      disabled: null,
      ariaLabel: (el, s) => el.call('assistantFlyCardActionLabel', s),
      title: (el, s) => el.call('assistantFlyCardActionLabel', s),
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('assistantFlyCardAct', s, e)
      },
      Icon: {
        name: (el, s) => {
          const a = s && s.kind !== 'skeleton' ? el.call('homeWidgetActionOf', s) : null
          return (a && a.icon) || 'arrowUpRight'
        }
      }
    },
    // The section's close: a drill section closes (its source stays), a
    // hand-made page's section leaves it (assistantFlyCardCloses).
    HomeWidgetAction_close: {
      if: (el, s) => !!el.call('assistantFlyCardCloses', s),
      dataBoardRemove: null,
      dataBoardDrillClose: (el, s) =>
        el.call('assistantFlyCardCloses', s) === 'drill' ? true : null,
      dataFlyRemove: (el, s) => (el.call('assistantFlyCardCloses', s) === 'remove' ? true : null),
      ariaLabel: (el, s) => el.call('assistantFlyCardCloseLabel', s),
      title: (el, s) => el.call('assistantFlyCardCloseLabel', s),
      disabled: (el, s) =>
        (el.call('assistantFlyPages', s.root) || []).some(
          (page) => page.key === s.page && page.save === 'saving'
        ),
      onClick: (e, el, s) => {
        e.stopPropagation()
        el.call('assistantFlyCardClose', s)
      },
      Icon: { name: 'x' }
    }
  },
  HomeWidgetEmpty: {
    Action: { hide: () => true }
  }
}
